import { FC, SVGProps, useMemo, useState } from 'react';
import { Button, ButtonSizes, ButtonStyles, EmptyState, Icons, List, ListItem } from '@silverfort/sf-components';
import { AD_ENTITY_TYPES, ScopeEntity, SCOPES, SCOPES_COLOR, SCOPES_TITLE } from './ScopeTypes';
import EntitiesDualSelectList, {
    TAB_OPTIONS,
    entity
} from '../../../../../../../../../sharedComponents/EntitiesDualSelectList';

const ENTITIES_ICONS: Record<AD_ENTITY_TYPES, FC<SVGProps<SVGSVGElement>>> = {
    [AD_ENTITY_TYPES.USER]: Icons.User,
    [AD_ENTITY_TYPES.GROUP]: Icons.Group,
    [AD_ENTITY_TYPES.OU]: Icons.Folder
};

interface ScopeComponentProps {
    entities: ScopeEntity[];
    onChange: (entities: ScopeEntity[]) => void;
    scope: SCOPES;
    subtitle?: string;
    mandatory?: boolean;
    disabled?: boolean;
}

const ScopeComponent = ({ entities, onChange, scope, subtitle, mandatory, disabled }: ScopeComponentProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
    const [menuOpen, setMenuOpen] = useState(false);

    const entitiesGuids = useMemo(() => entities.map((e) => e.guid), [entities]);

    const onSelectEntities = (entities: entity[] | null) => {
        onChange(
            entities
                ? entities.map((e) => ({
                      guid: e.guid,
                      display_name: e.name,
                      domain_name: e.domain_name,
                      type: e.type
                  }))
                : []
        );
    };

    const onRemoveEntity = (entity: ScopeEntity) => {
        onChange(entities.filter((e) => e.guid !== entity.guid));
    };

    return (
        <div className={`rounded-lg w-full border border-sf-gray-100 h-[280px] flex flex-col`}>
            <div className="flex justify-between items-center h-12 border-b border-sf-gray-100 px-4">
                <div className="flex text-sf items-center gap-2">
                    <div className={`h-2 w-2  rounded-full ${SCOPES_COLOR[scope]}`}></div>
                    <div>{`${SCOPES_TITLE[scope]} (${subtitle ? subtitle : entities.length})${
                        mandatory ? ' *' : ''
                    }`}</div>
                </div>
                <div>
                    <Button
                        onClick={(e) => {
                            setMenuOpen(true);
                            setAnchorEl(e.currentTarget);
                        }}
                        buttonStyle={ButtonStyles.Primary}
                        text="Add"
                        buttonSize={ButtonSizes.Small}
                        disabled={disabled}
                    />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto no-scrollbar px-4">
                {entities.length ? (
                    <List>
                        {entities.map((entity) => (
                            <ListItem
                                key={entity.guid}
                                title={entity.display_name}
                                subtitle={entity.domain_name}
                                IconLeft={ENTITIES_ICONS[entity.type]}
                                onRemove={!disabled ? () => onRemoveEntity(entity) : undefined}
                                disabled={disabled}
                            />
                        ))}
                    </List>
                ) : (
                    <EmptyState
                        className="w-full h-full"
                        title="No entities added"
                        description="Add accounts, groups or OUs to the scope"
                    />
                )}
            </div>
            {menuOpen && (
                <EntitiesDualSelectList
                    title={SCOPES_TITLE[scope]}
                    entitiesTabsOptions={[TAB_OPTIONS.ALL, TAB_OPTIONS.OUS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.USERS]}
                    onSelect={onSelectEntities}
                    selectedGuids={entitiesGuids}
                    onClose={() => {
                        setMenuOpen(false);
                    }}
                    menuProps={{
                        anchorEl: anchorEl,
                        open: true,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right' // menu's right edge aligns with button's left edge
                        }
                    }}
                />
            )}
        </div>
    );
};

export default ScopeComponent;
